import type { Link } from '@wix/public-schemas'
import type { PageLinkData } from './linksDataTypes'
import { addRelToLinkProps, removeHash } from './linkUtils'

export type PageLinkExtraData = {
	externalBaseUrl: string
	isMultilingualEnabled: boolean
	language: string
	originalLanguage: string
	languageResolutionMethod: string
	mainPageId: string
	currentPageId: string
	disableStaticPagesUrlHierarchy: boolean
	pageIdToHierarchyRoute: Record<string, string>
	shouldEncodeUris: boolean
	pageIdToIsPopup: Record<string, boolean>
	pageIdToPageUriSEO: Record<string, string>
}

export const resolvePageLinkHref = (
	_pageId: string,
	extraData: Pick<
		PageLinkExtraData,
		| 'externalBaseUrl'
		| 'mainPageId'
		| 'disableStaticPagesUrlHierarchy'
		| 'pageIdToHierarchyRoute'
		| 'shouldEncodeUris'
		| 'pageIdToPageUriSEO'
	>
) => {
	const {
		externalBaseUrl,
		mainPageId,
		disableStaticPagesUrlHierarchy,
		pageIdToHierarchyRoute,
		shouldEncodeUris,
		pageIdToPageUriSEO,
	} = extraData
	const pageId = removeHash(_pageId)
	const pageUriSEO = pageIdToPageUriSEO[pageId]
	const pagePath = disableStaticPagesUrlHierarchy ? pageUriSEO : pageIdToHierarchyRoute[pageId]

	const isMainPage = pageId === removeHash(mainPageId)
	const isMasterPage = pageId === 'masterPage'
	const isDeletedPage = !pagePath
	const shouldRedirectToHome = isMainPage || isMasterPage || isDeletedPage

	const href = shouldRedirectToHome ? externalBaseUrl : `${externalBaseUrl}/${pagePath}`

	return shouldEncodeUris ? encodeURI(href) : href
}

export const handlePageLink = (linkData: PageLinkData, extraData: PageLinkExtraData): Link => {
	const {
		pageIdToIsPopup,
		isMultilingualEnabled,
		language,
		originalLanguage,
		languageResolutionMethod,
		currentPageId,
	} = extraData
	const { pageId, target, rel } = linkData
	const queryParams = []
	const isLinkToPopup = pageIdToIsPopup[removeHash(pageId)]

	const href = resolvePageLinkHref(isLinkToPopup ? currentPageId : pageId, extraData)
	if (isMultilingualEnabled && language !== originalLanguage && languageResolutionMethod === 'QueryParam') {
		queryParams.push(`lang=${language}`)
	}
	if (isLinkToPopup) {
		queryParams.push(`popup=${removeHash(pageId)}`)
	}

	const hrefWithQueryParams = queryParams.length ? `${href}?${queryParams.join('&')}` : href

	const link = { href: hrefWithQueryParams, target }

	return addRelToLinkProps(link, rel)
}
